// A compose board in tiles mode, shared by the web and native views: slots
// in sentence order, the given words in place, chips filling the blanks.
import { faceText } from "@aybuben/core/face.ts";
import type { ComposeStep } from "@aybuben/core/lesson.ts";

/** Each slot's chip id, or null while a blank is empty. */
export type Slots = readonly (string | null)[];

export const startSlots = (step: ComposeStep): (string | null)[] => {
	const blanks = new Set(step.blanks);
	return step.tokens.map((tok, i) => (blanks.has(i) ? null : tok.id));
};

/** The chip into the first empty blank; null when every blank is full. */
export function placeChip(step: ComposeStep, slots: Slots, id: string): (string | null)[] | null {
	const blanks = new Set(step.blanks);
	const at = slots.findIndex((x, i) => blanks.has(i) && x === null);
	if (at < 0) return null;
	return slots.map((x, i) => (i === at ? id : x));
}

/** Empties a filled blank; null for a given word or an empty slot. */
export function clearSlot(step: ComposeStep, slots: Slots, i: number): (string | null)[] | null {
	if (!step.blanks.includes(i) || slots[i] === null || slots[i] === undefined) return null;
	return slots.map((x, j) => (j === i ? null : x));
}

/**
 * The answer the shell grades: null while any slot is empty, so an
 * incomplete board can never be submitted and counted as a mistake.
 */
export const boardAnswer = (slots: Slots): string[] | null =>
	slots.every((x): x is string => x !== null) ? [...slots] : null;

/** A chip's text: a placed chip came from the bank, a given word from the sentence. */
export function chipText(step: ComposeStep, id: string): string {
	const face =
		step.bank.find((o) => o.id === id)?.face ?? step.tokens.find((x) => x.id === id)?.face;
	return face ? faceText(face) : "";
}
